import React, { useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [errors, setErrors] = useState({});

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: "" }));
    }
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Please enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Please enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!isValidEmail(formData.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Please choose a subject.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please enter at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementById(firstErrorField);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmittedName(formData.firstName.trim());

    window.setTimeout(() => {
      setShowSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "",
        message: "",
      });
      setIsSubmitting(false);
    }, 900);
  };

  return (
    <main className="contact-page">
      <header className="contact-intro" aria-labelledby="contact-title">
        <p className="contact-eyebrow">Customer care</p>
        <h1 id="contact-title">Contact</h1>
        <p>
          Questions about an object, an order or working together? Our studio team
          would love to hear from you.
        </p>
      </header>

      <section className="contact-coordinates" aria-labelledby="contact-details-title">
        <p className="contact-eyebrow">Contact {siteConfig.brandName}</p>
        <h2 id="contact-details-title" className="contact-visually-hidden">
          Studio contact details
        </h2>
        <dl className="contact-coordinates__grid">
          <div>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>
            </dd>
          </div>
          <div>
            <dt>Studio</dt>
            <dd>{siteConfig.contact.address}</dd>
          </div>
          <div>
            <dt>Hours</dt>
            <dd>{siteConfig.contact.hours}</dd>
          </div>
        </dl>
        <p className="contact-coordinates__help">
          Looking for a quick answer? Read about <Link to="/shipping">shipping</Link>
          {" "}or review our <Link to="/returns">returns policy</Link>.
        </p>
      </section>

      <section className="contact-form-section" aria-labelledby="contact-form-title">
        <div className="contact-form-section__heading">
          <p className="contact-eyebrow">Send a message</p>
          <h2 id="contact-form-title">How can we help?</h2>
          <p>Fields marked with an asterisk are required.</p>
        </div>

        {showSuccess && (
          <div className="contact-success" role="status" aria-live="polite">
            <strong>Thank you, {submittedName}.</strong>
            <span>
              Your message passed the demo form validation. It has not been
              transmitted; please email {siteConfig.contact.email} for real support.
            </span>
          </div>
        )}

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="contact-form__row">
            <div className="contact-field">
              <label htmlFor="firstName">First name *</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.firstName)}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
              />
              {errors.firstName && (
                <span className="contact-field__error" id="firstName-error">
                  {errors.firstName}
                </span>
              )}
            </div>

            <div className="contact-field">
              <label htmlFor="lastName">Last name *</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.lastName)}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
              />
              {errors.lastName && (
                <span className="contact-field__error" id="lastName-error">
                  {errors.lastName}
                </span>
              )}
            </div>
          </div>

          <div className="contact-form__row">
            <div className="contact-field">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <span className="contact-field__error" id="email-error">
                  {errors.email}
                </span>
              )}
            </div>

          </div>

          <div className="contact-field">
            <label htmlFor="subject">Subject *</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              aria-invalid={Boolean(errors.subject)}
              aria-describedby={errors.subject ? "subject-error" : undefined}
            >
              <option value="">Choose a subject</option>
              <option value="order">Order support</option>
              <option value="product">Product question</option>
              <option value="trade">Trade and stockists</option>
              <option value="press">Press and collaborations</option>
              <option value="other">Something else</option>
            </select>
            {errors.subject && (
              <span className="contact-field__error" id="subject-error">
                {errors.subject}
              </span>
            )}
          </div>

          <div className="contact-field">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              rows="7"
              maxLength="1200"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Include an order reference when relevant."
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? "message-error" : "message-hint"}
            />
            <div className="contact-field__meta">
              <span id="message-hint">Please do not include payment details.</span>
              <span>{formData.message.length}/1200</span>
            </div>
            {errors.message && (
              <span className="contact-field__error" id="message-error">
                {errors.message}
              </span>
            )}
          </div>

          <p className="contact-form__demo-note">
            Demo frontend: this form validates in your browser but does not send,
            save or process personal information.
          </p>

          <button className="contact-submit" type="submit" disabled={isSubmitting}>
            <span>{isSubmitting ? "Checking message…" : "Send message"}</span>
            <span aria-hidden="true">↗</span>
          </button>
        </form>
      </section>
    </main>
  );
}

export default Contact;
